import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import classnames from 'classnames/bind';

import COLOR from '@CONSTANTS/COLOR.constant';
import { BASE_URL } from '@CONSTANTS/API.constant';

import useAuth from '@HOOKS/useAuth';
import { useGetAppSettingsQuery } from '@SERVICES';

import { ReactComponent as LogoutIcon } from '@ICONS/logout-icon.svg';
import { ReactComponent as MarriottLogo } from '@ICONS/Marriott-LOGO.svg';

import WorkspaceSwitcher from '@COMPONENTS/SHARED/WorkspaceSwitcher';

import styles from './MainHeader.module.scss';

const cx: CX = classnames.bind(styles);

function MainHeader() {
    const {
        actions, state: { user },
    } = useAuth();

    // Get app settings for dynamic logo, skip if no user is authenticated
    const { data: appSettings, isLoading, error } = useGetAppSettingsQuery(undefined, {
        skip: !user,
    });

    // Track if the logo image failed to load
    const [logoImageError, setLogoImageError] = useState(false);

    // Debug logging
    useEffect(() => {
        console.log('🔍 MainHeader Debug Info:');
        console.log('- User:', user ? 'authenticated' : 'not authenticated');
        console.log('- App Settings Loading:', isLoading);
        console.log('- App Settings Error:', error);
        console.log('- App Settings Data:', appSettings);
        console.log('- Header Logo Image (raw):', appSettings?.header_logo_image);
        console.log('- App Name:', appSettings?.app_name);
        console.log('- Logo Image Error State:', logoImageError);
        console.log('- BASE_URL:', BASE_URL);
    }, [user, isLoading, error, appSettings, logoImageError]);

    // Reset error state when app settings change (e.g., new logo uploaded)
    useEffect(() => {
        setLogoImageError(false);
    }, [appSettings?.header_logo_image]);

    // Construct full image URL from the path
    const getLogoImageUrl = (imagePath: string | null | undefined): string | null => {
        if (!imagePath) return null;
        
        // If it's already a full URL, return as is
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }
        
        // If it's a relative path, construct the full URL
        return `${BASE_URL}${imagePath}`;
    };

    const handleLogoImageError = () => {
        const fullUrl = getLogoImageUrl(appSettings?.header_logo_image);
        console.log('❌ Logo image failed to load:', fullUrl);
        setLogoImageError(true);
    };

    const renderHeaderContent = () => {
        console.log('🎨 Rendering header content...');
        
        // If there's a logo image URL and it hasn't failed to load, try to show it
        if (appSettings?.header_logo_image && !logoImageError) {
            const logoUrl = getLogoImageUrl(appSettings.header_logo_image);
            console.log('✅ Showing logo image:', logoUrl);
            
            if (logoUrl) {
                return (
                    <img
                        src={logoUrl}
                        alt="Application Logo"
                        className={cx('logo-image')}
                        onError={handleLogoImageError}
                        onLoad={() => console.log('✅ Logo image loaded successfully')}
                    />
                );
            }
        }

        // If there's an app name (either no logo URL or logo failed to load), show the app name as text
        if (appSettings?.app_name) {
            console.log('📝 Showing app name text:', appSettings.app_name);
            return (
                <span className={cx('app-name')}>
                    {appSettings.app_name}
                </span>
            );
        }

        // Fall back to the default Marriott logo
        console.log('🏢 Showing default Marriott logo');
        return (
            <MarriottLogo
                width={90}
                height="100%"
            />
        );
    };

    return (
        <div className={cx('main-header')}>
            <div className="container max-constraint center">
                <div className={cx('content-wrapper')}>
                    <div className={cx('title')}>
                        <Link to="/">
                            {renderHeaderContent()}
                        </Link>
                    </div>

                    <div className={cx('user-wrapper')}>
                        <div className={cx('user-name')}>
                            {user ? user.fullName : '?'}
                        </div>

                        <WorkspaceSwitcher />

                        <button
                            type="button"
                            className={cx('clear-button', 'logout-button')}
                            onClick={() => {
                                actions.logout();
                            }}
                        >
                            <LogoutIcon
                                fill={COLOR['app-white']}
                                width={20}
                                height={20}
                            />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MainHeader;
